import { ConfirmBanner } from '@/components/confirm-banner';
import type { CodexConnectionData } from '@/shared/types/workspace';
import { Banner } from '@/shared/ui/banner';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Pill } from '@/shared/ui/pill';
import { Spinner } from '@/shared/ui/spinner';
import { useCodexCard } from '../hooks/use-codex-card';
import { CODEX_STATUS } from '../model/connection-status';
import { AgentLogo } from './agent-logo';
import { CodexDetails } from './codex-details';
import { CodexOff } from './codex-off';

type CodexCardProps = { connection: CodexConnectionData };

/** Codex on the Connections page (design: Setup, the Codex card). */
export function CodexCard({ connection }: CodexCardProps) {
  const card = useCodexCard(connection);
  const status = CODEX_STATUS[connection.status];

  return (
    <Card asChild>
      <article data-enter="" aria-labelledby="cx-h" className="flex flex-col gap-3 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <AgentLogo agent="codex" />
          <span className="flex flex-col leading-4.5">
            <span id="cx-h" className="font-semibold text-section">
              Codex
            </span>
            <span className="text-meta text-muted">OpenAI Agents API</span>
          </span>
          <Pill tone={status.tone} hasDot isLive={card.isWaiting} className="ml-auto">
            {status.label}
          </Pill>
        </div>
        {card.isOn || card.isWaiting ? (
          <CodexDetails connection={connection} canCopySecret={card.isRotated || card.isWaiting} />
        ) : null}
        {card.isRotated ? (
          <div role="status">
            <Banner tone="ok" size="compact" title="New secret made.">
              Copy it into your Agents API environment. The old secret no longer verifies events.
            </Banner>
          </div>
        ) : null}
        {card.isWaiting ? (
          <Banner
            tone="info"
            size="compact"
            icon={<Spinner className="text-info-text" />}
            title="Waiting for the first event from Codex."
            action={
              <Button size="sm" isLoading={card.isChecking} onClick={() => void card.checkAgain()}>
                Check again
              </Button>
            }
          >
            Paste the URL and secret into your Agents API environment, then start a task.
          </Banner>
        ) : null}
        {card.isOff ? (
          <CodexOff isStarting={card.isStarting} onConnect={() => void card.connect()} />
        ) : null}
        {card.isConfirming ? (
          <ConfirmBanner
            id="dx-h"
            title="Disconnect Codex?"
            keepLabel="Keep connected"
            confirmLabel="Disconnect"
            isConfirming={card.isDisconnecting}
            onKeep={card.keepConnected}
            onConfirm={() => void card.disconnect()}
          >
            Events from Codex stop reaching Macswarm, and new Codex sessions will not get Pods.
            Running Pods finish their sessions.
          </ConfirmBanner>
        ) : null}
        {card.isOn && !card.isConfirming ? (
          <div className="flex flex-wrap gap-2">
            <Button isLoading={card.isRotating} onClick={() => void card.rotate()}>
              Rotate secret
            </Button>
            <Button variant="ghost" className="text-danger-text" onClick={card.askDisconnect}>
              Disconnect
            </Button>
          </div>
        ) : null}
      </article>
    </Card>
  );
}
